import { EMOTE_ACTIONS, EMOTE_EMOJI } from "../emotes/table.ts";
import { STAFF_BADGE_ICONS } from "../gm/badge.ts";
import { ICON_ART_SIZE } from "./art-scale.ts";
import { ICON_KINDS_LISTED, isIconKey, isIconKind, type IconKind } from "./icon-key.ts";

/**
 * The icons' pointer (ADR-111 decisions 6, 8 and 9): `assets/icons/current.json`, which the asset build writes and a
 * page reads before it draws colour art. It names the atlas by its content hash and, for every key of the icon
 * table, the art's kind, its cell in the atlas and the hash of its pixels. The atlas is one page of at most 30 by 30
 * cells on a pitch of 34 (the 32 pixel art with one transparent pixel around it), as tall as its highest cell needs;
 * a row of the table never moves, so a cell never does.
 *
 * A page reads this document from the network, so the validator is strict: a field it does not know is refused at
 * every level, every count and number is held to a bound, and what passes is rebuilt field by field in one order.
 * The icons are a list, not a record, so a key listed twice is seen and refused rather than lost in parsing.
 *
 * Shape is not completeness: `iconManifestProblems` holds a pointer against the rules, the emote table and the staff
 * badges, and a publisher refuses one that lacks a key any of them names. Keys neither names are accepted and ignored, which is what
 * lets a page of an older build read a newer pointer. A change of `version` is one thing such a page cannot read: it
 * refuses the pointer and draws glyphs. An entry of a kind the page's build does not know is the other, since the
 * validator refuses the whole pointer for it: a kind is added to `ICON_KINDS` in a build that ships before any
 * pointer holds a row of it.
 */

/**
 * The pointer's version. A second page, or one atlas per kind, would move it.
 */
export const ICON_MANIFEST_VERSION = 1;

/**
 * Where the atlas and its pointer sit, relative to the asset base and under `dist/`.
 */
export const ICONS_DIR = "assets/icons";

/**
 * The atlas's grid: the distance in pixels from one cell to the next, and how many cells one page holds across and
 * down.
 */
export const ICON_ATLAS_PITCH = 34;
export const ICON_ATLAS_COLUMNS = 30;
export const ICON_ATLAS_ROWS = 30;

const MAX_ICONS = ICON_ATLAS_COLUMNS * ICON_ATLAS_ROWS;
const MAX_SHOWN_CHARS = 60;
const HASH = /^[0-9a-f]{16}$/;
const ATLAS_PATH = new RegExp(`^${ICONS_DIR}/atlas\\.[0-9a-f]{16}\\.png$`);

/**
 * One icon of the pointer: its key, its kind (one of `ICON_KINDS`), its cell (the number of its row in the icon
 * table, from 0) and the first 16 hex digits of the hash of its decoded pixels.
 */
export interface IconManifestEntry {
  readonly key: string;
  readonly kind: IconKind;
  readonly cell: number;
  readonly hash: string;
}

/**
 * The pointer: its version, the art's size, the atlas's pitch, the atlas's hashed path relative to the asset base,
 * and the icons in the order listed.
 */
export interface IconManifest {
  readonly version: typeof ICON_MANIFEST_VERSION;
  readonly size: typeof ICON_ART_SIZE;
  readonly pitch: typeof ICON_ATLAS_PITCH;
  readonly atlas: string;
  readonly icons: readonly IconManifestEntry[];
}

/**
 * Where a key's art stands in the atlas: its kind and the top left pixel of its 32 by 32 art.
 */
export interface IconPlace {
  readonly kind: IconKind;
  readonly x: number;
  readonly y: number;
}

/**
 * A record of the rules that may name an icon: an item, an ability or an effect.
 */
export interface IconNamer {
  readonly id: string;
  readonly icon?: string | null;
}

/**
 * An emoji as `iconManifestProblems` reads it: its name and the key of its art.
 */
export interface IconEmoji {
  readonly name: string;
  readonly key: string;
}

/**
 * The part of an action `iconManifestProblems` reads: the word on its button and the key of its picture.
 */
export interface IconAction {
  readonly word: string;
  readonly emoji: string;
}

/**
 * The part of the rules `iconManifestProblems` reads: the three tables whose records may name an icon.
 */
export interface IconNamingRules {
  readonly items: ReadonlyMap<string, IconNamer>;
  readonly abilities: ReadonlyMap<string, IconNamer>;
  readonly effects: ReadonlyMap<string, IconNamer>;
}

/**
 * The path of the atlas's file.
 *
 * @param hash The 16 hex digits of its content hash.
 * @returns The path relative to the asset base.
 * @example
 * iconAtlasPath("0123456789abcdef");
 * // "assets/icons/atlas.0123456789abcdef.png"
 */
export function iconAtlasPath(hash: string): string {
  return `${ICONS_DIR}/atlas.${hash}.png`;
}

/**
 * Narrows to a plain object that holds no field but those named.
 *
 * @param value The value.
 * @param where Its name, for the error.
 * @param known The fields it may hold.
 * @returns The object.
 * @throws Error when it is not an object, or naming the first field it holds that is not known.
 */
function asObject(value: unknown, where: string, known: readonly string[]): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${where} must be an object`);
  }
  const unknown = Object.keys(value).find((key) => !known.includes(key));
  if (unknown !== undefined) {
    throw new Error(`${where} has an unknown field ${unknown.slice(0, MAX_SHOWN_CHARS)}`);
  }
  return value as Record<string, unknown>;
}

/**
 * A value as an error shows it: its JSON, cut short.
 *
 * @param value The value.
 * @returns The text.
 */
function shown(value: unknown): string {
  return String(JSON.stringify(value)).slice(0, MAX_SHOWN_CHARS);
}

/**
 * Reads one icon of the pointer.
 *
 * @param value The parsed entry.
 * @param index Its place in the list.
 * @param keys The place of each key read so far, to which this entry's is added.
 * @param cells The place of each cell read so far, to which this entry's is added.
 * @returns The entry.
 * @throws Error naming the entry and its fault: not an object, an unknown field, a key that is not an icon's key or
 *   that an earlier entry holds, a kind that is not a kind, a cell that is not a whole number inside the page or
 *   that an earlier entry holds, a hash that is not 16 hex digits.
 */
function entryOf(value: unknown, index: number, keys: Map<string, number>, cells: Map<number, number>): IconManifestEntry {
  const fields = asObject(value, `icon manifest icons[${index}]`, ["key", "kind", "cell", "hash"]);
  const { key, kind, cell, hash } = fields;
  if (!isIconKey(key)) {
    throw new Error(`icon manifest icons[${index}].key ${shown(key)} is not an icon's key`);
  }
  const where = `icon manifest icons[${index}] (${key})`;
  if (keys.has(key)) {
    throw new Error(`${where}: the key is icons[${keys.get(key)}]'s too`);
  }
  if (!isIconKind(kind)) {
    throw new Error(`${where}: kind ${shown(kind)} must be ${ICON_KINDS_LISTED}`);
  }
  if (typeof cell !== "number" || !Number.isSafeInteger(cell) || cell < 0 || cell >= MAX_ICONS) {
    throw new Error(`${where}: cell ${String(cell).slice(0, MAX_SHOWN_CHARS)} lies outside the atlas, whose cells are 0 to ${MAX_ICONS - 1}`);
  }
  if (cells.has(cell)) {
    throw new Error(`${where}: cell ${cell} is icons[${cells.get(cell)}]'s too`);
  }
  if (typeof hash !== "string" || !HASH.test(hash)) {
    throw new Error(`${where}: hash must be 16 hex digits`);
  }
  keys.set(key, index);
  cells.set(cell, index);
  return { key, kind, cell, hash };
}

/**
 * Validates a parsed icons' pointer (`IconManifest`). It checks the shape alone; whether the pointer holds the keys
 * the rules name is `iconManifestProblems`.
 *
 * @param value The parsed document.
 * @returns The pointer, rebuilt in the type's field order with its icons in the order they were listed.
 * @throws Error naming the first part at fault: a version other than `ICON_MANIFEST_VERSION` (judged before anything
 *   else, so a page says of a newer pointer only that it is newer), a field the pointer does not know, a `size`
 *   other than 32 or a `pitch` other than 34, an atlas that is not the hashed file under `assets/icons`, more icons
 *   than a page holds, a key listed twice, a cell outside the atlas or held twice, or an entry that does not read.
 * @example
 * validateIconManifest({ version: 1, size: 32, pitch: 34, atlas: "assets/icons/atlas.0123456789abcdef.png", icons: [] });
 * // the same pointer
 */
export function validateIconManifest(value: unknown): IconManifest {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("icon manifest must be an object");
  }
  const version = (value as Record<string, unknown>)["version"];
  if (version !== ICON_MANIFEST_VERSION) {
    throw new Error(`icon manifest version ${String(version).slice(0, MAX_SHOWN_CHARS)} is not ${ICON_MANIFEST_VERSION}`);
  }
  const fields = asObject(value, "icon manifest", ["version", "size", "pitch", "atlas", "icons"]);
  if (fields["size"] !== ICON_ART_SIZE) {
    throw new Error(`icon manifest size ${String(fields["size"]).slice(0, MAX_SHOWN_CHARS)} is not ${ICON_ART_SIZE}`);
  }
  if (fields["pitch"] !== ICON_ATLAS_PITCH) {
    throw new Error(`icon manifest pitch ${String(fields["pitch"]).slice(0, MAX_SHOWN_CHARS)} is not ${ICON_ATLAS_PITCH}`);
  }
  const atlas = fields["atlas"];
  if (typeof atlas !== "string" || !ATLAS_PATH.test(atlas)) {
    throw new Error(`icon manifest atlas must be ${ICONS_DIR}/atlas.<16 hex>.png`);
  }
  const listed = fields["icons"];
  if (!Array.isArray(listed)) {
    throw new Error("icon manifest icons must be a list");
  }
  if (listed.length > MAX_ICONS) {
    throw new Error(`icon manifest icons must hold at most ${MAX_ICONS}`);
  }
  const keys = new Map<string, number>();
  const cells = new Map<number, number>();
  const icons = listed.map((entry, index) => entryOf(entry, index, keys, cells));
  return { version: ICON_MANIFEST_VERSION, size: ICON_ART_SIZE, pitch: ICON_ATLAS_PITCH, atlas, icons };
}

/**
 * Where a key's art stands in the atlas: cell n is at column n mod 30 and row floor(n / 30) on the pointer's pitch,
 * and the art lies inside its cell's transparent border.
 *
 * @param manifest The validated pointer.
 * @param key The icon's key.
 * @returns The art's kind and the top left pixel of its 32 by 32 pixels, or null when the pointer lacks the key, for
 *   which a page draws the glyph (ADR-111 decision 16).
 * @example
 * iconCell(manifest, "tile-dizzy-head");
 * // { kind: "tile", x: 35, y: 1 } for the table's second row
 */
export function iconCell(manifest: IconManifest, key: string): IconPlace | null {
  const icon = manifest.icons.find((entry) => entry.key === key);
  if (icon === undefined) {
    return null;
  }
  const border = (manifest.pitch - manifest.size) / 2;
  return { kind: icon.kind, x: (icon.cell % ICON_ATLAS_COLUMNS) * manifest.pitch + border, y: Math.floor(icon.cell / ICON_ATLAS_COLUMNS) * manifest.pitch + border };
}

/**
 * Holds a pointer against the rules, the emote table and the staff badges: every key an item, an ability, an effect, an
 * emoji, an action's picture or a staff badge names has an icon in it. A publisher runs it before it uploads, since a
 * page would otherwise fall back to a glyph for a thing the release meant to draw, and an emoji has no glyph to fall
 * back to (ADR-114 decision 5.3). Keys the pointer holds and nothing names are not a problem.
 *
 * @param rules The rules' items, abilities and effects.
 * @param manifest The validated pointer.
 * @param emoji The emoji whose keys the pointer must hold; the emote table's unless given.
 * @param actions The actions whose pictures the pointer must hold; the emote table's unless given.
 * @param badges The key of each staff badge by its number; `STAFF_BADGE_ICONS` unless given.
 * @returns One phrase per key the pointer lacks, with every thing that names it, in the order a key is first named
 *   (items, then abilities, then effects, then emoji, then actions, then badges); empty when the pointer holds them
 *   all.
 * @example
 * iconManifestProblems(rules, { ...manifest, icons: [] }, [], [], {});
 * // ["flask-red-tilted (item minor_healing_potion)"]
 */
export function iconManifestProblems(rules: IconNamingRules, manifest: IconManifest, emoji: readonly IconEmoji[] = EMOTE_EMOJI, actions: readonly IconAction[] = EMOTE_ACTIONS, badges: Readonly<Record<number, string>> = STAFF_BADGE_ICONS): string[] {
  const held = new Set(manifest.icons.map((icon) => icon.key));
  const namers = new Map<string, string[]>();

  /**
   * Notes a key the pointer lacks under what names it.
   *
   * @param key The key named, or null for none.
   * @param user What names it, as the phrase says it.
   */
  const note = (key: string | null, user: string): void => {
    if (key !== null && !held.has(key)) {
      namers.set(key, [...(namers.get(key) ?? []), user]);
    }
  };
  const tables: readonly (readonly [string, ReadonlyMap<string, IconNamer>])[] = [["item", rules.items], ["ability", rules.abilities], ["effect", rules.effects]];
  for (const [what, table] of tables) {
    for (const record of table.values()) {
      note(record.icon ?? null, `${what} ${record.id}`);
    }
  }
  for (const one of emoji) {
    note(one.key, `emoji ${one.name}`);
  }
  for (const one of actions) {
    note(one.emoji, `action ${one.word}`);
  }
  for (const [badge, key] of Object.entries(badges)) {
    note(key, `badge ${badge}`);
  }
  return [...namers.entries()].map(([key, users]) => `${key} (${users.join(", ")})`);
}
